/* eslint-disable @typescript-eslint/no-floating-promises */

'use client'

import { useState } from 'react'
import { AckoMainTheme, ThemeProvider } from '@acko-tech/building-blocks.ui.theme'
import Image from 'next/image'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { FUEL_TAP_EVENTS } from '@constants/fuelEvents'
import { fuelSegmentEventCommonPropFunc } from '@utils/segmentProperties'
import { uppercaseFirstLetter } from '@utils/stringUtils'
import {
  appendCodeToCommonCitiesMultipleStates,
  defaultSelectedCity,
  defaultSelectedState,
  FuelType,
  getStateName,
} from '@helpers/fuelPageHelpers'
import { getCurrentDate } from '@utils/date'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown'
import { eventProps } from '@helpers/fuelDropDownHelper'
import styles from './dropDownWidget.module.scss'
import {
  DEFAULT_DIESEL,
  DEFAULT_PETROL,
  DESERT_LEFT,
  DESERT_RIGHT,
  DIESEL,
  PETROL,
  RUPEE_SIGN,
  UNIT,
} from './constants/texts'

export const DynamicDropdownWidget = ({
  location,
  fuelPageResponse,
  fuelType,
  parsedLocationIsState,
  getAllStatesIndiaResponse,
  mappedStateWithCities,
  stateCode,
}) => {
  const fuelStateArray = JSON.parse(getAllStatesIndiaResponse)

  const defaultState = defaultSelectedState(
    parsedLocationIsState,
    location,
    mappedStateWithCities,
    fuelStateArray,
    stateCode
  )
  const [selectedState, setSelectedState] = useState<any>({
    displayValue: defaultState?.setDisplayValue,
    value: defaultState?.setValue,
  })

  const defaultCity = defaultSelectedCity(parsedLocationIsState, location, mappedStateWithCities, stateCode)

  const [selectedCity] = useState<any>({
    displayValue: defaultCity?.setDisplayCity,
    value: defaultCity?.setValue,
    petrol_price: defaultCity?.petrolPrice,
    diesel_price: defaultCity?.dieselPrice,
  })

  const [showState, setShowState] = useState(false)
  const [showCity, setShowCity] = useState(false)
  const [fuelTypeInPage] = useState(fuelType)

  // Get list of all the cities with it's petrol/diesel price based on the selected state.
  const stateName = selectedState?.displayValue
  const listOfCities = mappedStateWithCities[getStateName(stateName)]

  const handleFuelTypeEvents = async fuelType => {
    const fuelSegmentCommonProperties = await fuelSegmentEventCommonPropFunc()
    await trackEvent(FUEL_TAP_EVENTS.TAP_PETROL_DIESEL, {
      ...eventProps(selectedState?.displayValue, selectedCity?.displayValue, fuelType),
      ...fuelSegmentCommonProperties,
    })
      .then(() => {})
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        window.location.href = `/fuel/${appendCodeToCommonCitiesMultipleStates(
          fuelType,
          selectedState?.displayValue,
          !parsedLocationIsState ? selectedCity?.displayValue : location,
          fuelPageResponse?.attributes?.commonCitiesMultipleStates,
          listOfCities
        )}/`
      })
  }
  return (
    <ThemeProvider theme={AckoMainTheme}>
      <div className={styles.fuelPricesWidgetContainer}>
        {/* Dessert Patterns */}
        <div className={styles.fuelDessertLeftPatternImageContainer}>
          <Image height={240} width={432} src={DESERT_LEFT} alt="Icon" loading="lazy" layout="fixed" />
        </div>
        <div className={styles.fuelDessertRightPatternImageContainer}>
          <Image height={240} width={432} src={DESERT_RIGHT} alt="Icon" loading="lazy" layout="fixed" />
        </div>
        <div className={styles.todayPriceHeading}>
          {`Today's ${fuelTypeInPage} price in ${uppercaseFirstLetter(location)}`}
        </div>
        <div className={styles.todayPriceHeadingDate}>{`as on ${getCurrentDate()}`}</div>
        <div className={styles.dropdownAlign}>
          <div className={styles.dropdownAlign2}>
            <Dropdown
              label="State"
              className="dropdown-styling-1"
              value={selectedState}
              show={showState}
              options={fuelStateArray?.map(item => ({
                displayValue: item?.state_name,
                value: item?.state_code,
              }))}
              hideHeader
              hideClose={false}
              onChange={option => {
                setSelectedState(option)
                trackEvent(FUEL_TAP_EVENTS.TAP_STATE, eventProps(option.displayValue, '', fuelTypeInPage))
                  .then(() => {})
                  .catch(err => {
                    console.error(err)
                  })
              }}
              toggleDropdown={show => setShowState(show)}
              showLabelAfterSelect={false}
              showSearchBar
            />
          </div>
          <div className={styles.dropdownAlign2}>
            <Dropdown
              label="City"
              className="dropdown-styling-2"
              value={selectedCity}
              show={showCity}
              options={
                listOfCities
                  ? Object.keys(listOfCities).map(key => ({
                      displayValue: uppercaseFirstLetter(key),
                      value: uppercaseFirstLetter(key),
                      petrol_price: listOfCities[key].petrolPrice,
                      diesel_price: listOfCities[key].dieselPrice,
                    }))
                  : [{ displayValue: '', value: '' }]
              }
              hideHeader
              hideClose
              onChange={option => {
                trackEvent(
                  FUEL_TAP_EVENTS.TAP_CITY,
                  eventProps(selectedState.displayValue, option.displayValue, fuelTypeInPage)
                )
                  .then(() => {})
                  .catch(err => {
                    console.error(err)
                  })
                  .finally(() => {
                    window.location.href = `/fuel/${appendCodeToCommonCitiesMultipleStates(
                      fuelTypeInPage,
                      selectedState?.displayValue,
                      option.displayValue,
                      fuelPageResponse?.attributes?.commonCitiesMultipleStates,
                      listOfCities
                    )}/`
                  })
              }}
              toggleDropdown={show => setShowCity(show)}
              showLabelAfterSelect={false}
              showSearchBar
            />
          </div>
        </div>
        <div className={styles.selectFuelType}>
          <div
            className={`${styles.fuelType} ${fuelType === FuelType.Petrol && styles.currentFuel}`}
            onClick={() => {
              handleFuelTypeEvents(FuelType.Petrol)
            }}>
            {PETROL}
          </div>
          <div
            className={`${styles.fuelType} ${fuelType === FuelType.Diesel && styles.currentFuel}`}
            onClick={() => {
              handleFuelTypeEvents(FuelType.Diesel)
            }}>
            {DIESEL}
          </div>
        </div>
        {/* LIVE Fuel prices for petrol/diesel */}
        <div className={styles.fuelPriceContainer}>
          <div className={styles.fuelPriceSymbol}>{RUPEE_SIGN}</div>
          <div className={styles.fuelPriceValue}>
            {fuelTypeInPage === FuelType.Petrol
              ? selectedCity?.petrol_price || DEFAULT_PETROL
              : selectedCity?.diesel_price || DEFAULT_DIESEL}
          </div>
          <div className={styles.fuelPriceSymbol}>{UNIT}</div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default DynamicDropdownWidget
